// Meta-Fortschritt (kapitelübergreifend): Taler, Ausbau der Kneipe, Gäste, Sammelobjekte, Erfolge, Wissens-Statistik, Logbuch.
// Rein und testbar; Speicherung übernimmt der Client (lokal) bzw. ab S8 das Konto.

export interface LogEntry {
  qid: string;
  prompt: string;
  category: string;
  answer: string;
  right: boolean;
  explanation: string;
  source: { title: string; url: string };
  at: number;
  mode: 'adventure' | 'show' | 'daily' | 'training' | 'party';
}

export interface Profile {
  coins: number;
  earned: number;
  upgrades: string[];
  collectibles: string[];
  achievements: Record<string, number>;
  stats: Record<string, { asked: number; right: number }>;
  log: LogEntry[];
  counters: Record<string, number>;
  streak: number;
  bestStreak: number;
  /** Gedächtnis der Stammgäste (S9) */
  memory: Record<string, NpcMemory>;
  /** Story-Wege, die durch Gespräche und Kneipenspiele geöffnet wurden (id → Zeitpunkt) */
  story: Record<string, number>;
}

export interface NpcMemory {
  met: number;
  lastAt: number;
  lastLevel: number;
  /** Zeitpunkt des letzten Logbuch-Eintrags, den die Figur „kennt“ */
  seenLogAt: number;
  games: { won: number; lost: number; last?: { game: string; won: boolean; at: number } };
  /** letzte Sätze der Spielerin im Smalltalk */
  talk: string[];
}

export function newProfile(): Profile {
  return { coins: 0, earned: 0, upgrades: [], collectibles: [], achievements: {}, stats: {}, log: [], counters: {}, streak: 0, bestStreak: 0, memory: {}, story: {} };
}

// ---------------- Ausbau ----------------

export interface Upgrade {
  id: string;
  kind: 'repair' | 'deco';
  cost: number;
  requires?: string;
}

/** 24 Ausbau- und Deko-Elemente – jedes ist in den Szenen sichtbar (Flag up_<id>) */
export const UPGRADES: Upgrade[] = [
  { id: 'clean', kind: 'repair', cost: 10 },
  { id: 'window', kind: 'repair', cost: 15 },
  { id: 'lights', kind: 'repair', cost: 20 },
  { id: 'stove', kind: 'repair', cost: 20 },
  { id: 'walls', kind: 'repair', cost: 30, requires: 'clean' },
  { id: 'floor', kind: 'repair', cost: 30, requires: 'clean' },
  { id: 'jukebox', kind: 'repair', cost: 25 },
  { id: 'stools', kind: 'repair', cost: 20 },
  { id: 'tables', kind: 'repair', cost: 30, requires: 'stools' },
  { id: 'taps', kind: 'repair', cost: 35 },
  { id: 'shelf', kind: 'repair', cost: 30, requires: 'taps' },
  { id: 'darts', kind: 'repair', cost: 15 },
  { id: 'plants', kind: 'deco', cost: 15 },
  { id: 'garland', kind: 'deco', cost: 20 },
  { id: 'candles', kind: 'deco', cost: 15, requires: 'tables' },
  { id: 'net', kind: 'deco', cost: 25 },
  { id: 'flags', kind: 'deco', cost: 25 },
  { id: 'ships', kind: 'deco', cost: 30 },
  { id: 'photos', kind: 'deco', cost: 25, requires: 'walls' },
  { id: 'carpet', kind: 'deco', cost: 30, requires: 'floor' },
  { id: 'lamps', kind: 'deco', cost: 35, requires: 'lights' },
  { id: 'vitrine', kind: 'deco', cost: 50, requires: 'photos' },
  { id: 'sign', kind: 'deco', cost: 40, requires: 'walls' },
  { id: 'bell', kind: 'deco', cost: 60, requires: 'vitrine' },
];

/** Ausbaustufen 0–5 nach Anzahl der Ausbauten */
export const LEVELS = [0, 4, 9, 14, 19, 24];

export function pubLevel(p: Profile): number {
  let l = 0;
  for (let i = 0; i < LEVELS.length; i++) if (p.upgrades.length >= LEVELS[i]) l = i;
  return l;
}

export function canBuy(p: Profile, id: string): boolean {
  const u = UPGRADES.find((x) => x.id === id);
  if (!u || p.upgrades.includes(id)) return false;
  if (u.requires && !p.upgrades.includes(u.requires)) return false;
  return p.coins >= u.cost;
}

export function buy(p: Profile, id: string): boolean {
  if (!canBuy(p, id)) return false;
  const u = UPGRADES.find((x) => x.id === id)!;
  p.coins -= u.cost;
  p.upgrades.push(id);
  return true;
}

/** Gäste, die ab einer Ausbaustufe im Schankraum auftauchen (und im Quizabend mitspielen) */
export const GUESTS: { id: string; outfit: string; level: number }[] = [
  { id: 'gesa', outfit: 'gesa', level: 0 },
  { id: 'jan', outfit: 'jan', level: 1 },
  { id: 'ole', outfit: 'ole', level: 2 },
  { id: 'pieper', outfit: 'hinnerk', level: 3 },
  { id: 'ilse', outfit: 'gesa', level: 4 },
  { id: 'brakenhoff', outfit: 'brakenhoff', level: 5 },
];
export const guestsFor = (p: Profile) => GUESTS.filter((g) => g.level <= pubLevel(p)).map((g) => g.id);

/** Flags, die die Szenen sehen (Ausbau, Stufe, Gäste) */
export function metaFlags(p: Profile): Record<string, boolean | number> {
  const f: Record<string, boolean | number> = { pub_level: pubLevel(p) };
  for (const u of p.upgrades) f['up_' + u] = true;
  for (const g of guestsFor(p)) f['guest_' + g] = true;
  for (const c of p.collectibles) f['col_' + c] = true;
  for (const k of Object.keys(p.story ?? {})) f['story_' + k] = true;
  // Verbündete aus Nebenaufgaben der Stadt (kapitelübergreifend, für die Enden)
  f.allies = Object.keys(p.story ?? {}).filter((k) => k.startsWith('ally_')).length;
  return f;
}

/** Trinkgeld nach einem Quizabend: je schöner die Kneipe, desto mehr Gäste, desto mehr Taler */
export const showTip = (p: Profile) => 20 + 10 * pubLevel(p) + 5 * guestsFor(p).length;

// ---------------- Antworten, Taler, Statistik ----------------

export const COINS_PER_RIGHT = 10;

export function recordAnswer(p: Profile, e: Omit<LogEntry, 'at'> & { at?: number }) {
  const s = (p.stats[e.category] ??= { asked: 0, right: 0 });
  s.asked++;
  if (e.right) {
    s.right++;
    p.coins += COINS_PER_RIGHT;
    p.earned += COINS_PER_RIGHT;
    p.streak++;
    p.bestStreak = Math.max(p.bestStreak, p.streak);
  } else p.streak = 0;
  p.log.unshift({ ...e, at: e.at ?? Date.now() });
  if (p.log.length > 500) p.log.length = 500;
  bump(p, 'answers');
  if (e.right) bump(p, 'right');
  bump(p, 'mode_' + e.mode);
}

export function bump(p: Profile, key: string, n = 1) {
  p.counters[key] = (p.counters[key] ?? 0) + n;
}

export function collect(p: Profile, id: string): boolean {
  if (p.collectibles.includes(id)) return false;
  p.collectibles.push(id);
  p.coins += 15;
  p.earned += 15;
  return true;
}

// ---------------- Erfolge ----------------

export interface Achievement {
  id: string;
  test: (p: Profile) => boolean;
}

const c = (p: Profile, k: string) => p.counters[k] ?? 0;
const catMaster = (p: Profile, n: number) => Object.values(p.stats).filter((s) => s.right >= 10 && s.right / s.asked >= 0.7).length >= n;

/** 43 Erfolge */
export const ACHIEVEMENTS: Achievement[] = [
  { id: 'first_answer', test: (p) => c(p, 'answers') >= 1 },
  { id: 'first_right', test: (p) => c(p, 'right') >= 1 },
  { id: 'right_10', test: (p) => c(p, 'right') >= 10 },
  { id: 'right_50', test: (p) => c(p, 'right') >= 50 },
  { id: 'right_100', test: (p) => c(p, 'right') >= 100 },
  { id: 'right_250', test: (p) => c(p, 'right') >= 250 },
  { id: 'streak_5', test: (p) => p.bestStreak >= 5 },
  { id: 'streak_10', test: (p) => p.bestStreak >= 10 },
  { id: 'streak_20', test: (p) => p.bestStreak >= 20 },
  { id: 'cats_5', test: (p) => Object.keys(p.stats).length >= 5 },
  { id: 'cats_all', test: (p) => Object.keys(p.stats).length >= 15 },
  { id: 'master_1', test: (p) => catMaster(p, 1) },
  { id: 'master_3', test: (p) => catMaster(p, 3) },
  { id: 'coins_100', test: (p) => p.earned >= 100 },
  { id: 'coins_500', test: (p) => p.earned >= 500 },
  { id: 'coins_1000', test: (p) => p.earned >= 1000 },
  { id: 'first_upgrade', test: (p) => p.upgrades.length >= 1 },
  { id: 'level_1', test: (p) => pubLevel(p) >= 1 },
  { id: 'level_2', test: (p) => pubLevel(p) >= 2 },
  { id: 'level_3', test: (p) => pubLevel(p) >= 3 },
  { id: 'level_4', test: (p) => pubLevel(p) >= 4 },
  { id: 'level_5', test: (p) => pubLevel(p) >= 5 },
  { id: 'all_repairs', test: (p) => UPGRADES.filter((u) => u.kind === 'repair').every((u) => p.upgrades.includes(u.id)) },
  { id: 'all_deco', test: (p) => UPGRADES.filter((u) => u.kind === 'deco').every((u) => p.upgrades.includes(u.id)) },
  { id: 'coaster_1', test: (p) => p.collectibles.length >= 1 },
  { id: 'coaster_6', test: (p) => p.collectibles.length >= 6 },
  { id: 'coaster_all', test: (p) => p.collectibles.length >= 12 },
  { id: 'chapter_1', test: (p) => c(p, 'chapter_ch1') >= 1 },
  { id: 'chapter_2', test: (p) => c(p, 'chapter_ch2') >= 1 },
  { id: 'show_played', test: (p) => c(p, 'shows') >= 1 },
  { id: 'show_5', test: (p) => c(p, 'shows') >= 5 },
  { id: 'daily_1', test: (p) => c(p, 'mode_daily') >= 1 },
  { id: 'training_3', test: (p) => c(p, 'trainings') >= 3 },
  { id: 'logbook_100', test: (p) => p.log.length >= 100 },
  { id: 'chapter_3', test: (p) => c(p, 'chapter_ch3') >= 1 },
  { id: 'chapter_4', test: (p) => c(p, 'chapter_ch4') >= 1 },
  { id: 'chapter_5', test: (p) => ['ch5_anker', 'ch5_verkauf', 'ch5_genossenschaft'].some((e) => c(p, 'chapter_' + e) >= 1) },
  { id: 'all_endings', test: (p) => ['ch5_anker', 'ch5_verkauf', 'ch5_genossenschaft'].every((e) => c(p, 'chapter_' + e) >= 1) },
  { id: 'allies_6', test: (p) => Object.keys(p.story ?? {}).filter((k) => k.startsWith('ally_')).length >= 6 },
  { id: 'smalltalk', test: (p) => c(p, 'talks') >= 1 },
  { id: 'game_won', test: (p) => c(p, 'games_won') >= 1 },
  { id: 'games_5', test: (p) => c(p, 'games') >= 5 },
  { id: 'secret', test: (p) => Object.keys(p.story ?? {}).length >= 1 },
];

/** neu freigeschaltete Erfolge eintragen und zurückgeben */
export function checkAchievements(p: Profile, now = Date.now()): string[] {
  const out: string[] = [];
  for (const a of ACHIEVEMENTS) if (!p.achievements[a.id] && a.test(p)) ((p.achievements[a.id] = now), out.push(a.id));
  return out;
}

/** Sammelobjekte: 12 seltene Bierdeckel, versteckt in Szenen beider Kapitel */
export const COASTERS = ['c01', 'c02', 'c03', 'c04', 'c05', 'c06', 'c07', 'c08', 'c09', 'c10', 'c11', 'c12'];

// ---------------- Gedächtnis der Stammgäste (S9) ----------------

export const FAV_CATEGORY: Record<string, string> = {
  gesa: 'Essen & Trinken',
  jan: 'Literatur & Sprache',
  ole: 'Seefahrt & Häfen',
  pieper: 'Geografie',
  ilse: 'Geschichte',
  brakenhoff: 'Sport',
};

export function memoryOf(p: Profile, npc: string): NpcMemory {
  p.memory ??= {};
  return (p.memory[npc] ??= { met: 0, lastAt: 0, lastLevel: 0, seenLogAt: 0, games: { won: 0, lost: 0 }, talk: [] });
}

export interface Greeting {
  key: string;
  vars: Record<string, string | number>;
}

/** Begrüßung aus dem Gedächtnis: Die Figur reagiert auf frühere Antworten, Spiele, Erfolge und Begegnungen */
export function greeting(p: Profile, npc: string): Greeting {
  const m = p.memory?.[npc];
  if (!m || m.met === 0) return { key: 'first', vars: {} };
  const fav = FAV_CATEGORY[npc];
  const fresh = p.log.filter((e) => e.at > m.seenLogAt);
  const wrongFav = fresh.find((e) => !e.right && e.category === fav);
  if (wrongFav) return { key: 'wrongFav', vars: { prompt: wrongFav.prompt, answer: wrongFav.answer } };
  if (m.games.last && m.games.last.at > m.lastAt) return { key: m.games.last.won ? 'lostGame' : 'wonGame', vars: { game: m.games.last.game } };
  const newAch = Object.entries(p.achievements).filter(([, at]) => at > m.lastAt).sort((a, b) => b[1] - a[1])[0];
  if (newAch) return { key: 'achievement', vars: { ach: newAch[0] } };
  if (pubLevel(p) > m.lastLevel) return { key: 'level', vars: { level: pubLevel(p) } };
  const rightFav = fresh.find((e) => e.right && e.category === fav);
  if (rightFav) return { key: 'rightFav', vars: { prompt: rightFav.prompt, answer: rightFav.answer } };
  if (m.talk.length) return { key: 'lastTalk', vars: { said: m.talk[m.talk.length - 1].slice(0, 60) } };
  return { key: 'again', vars: { n: m.met + 1 } };
}

/** Begegnung merken (nach der Begrüßung) */
export function noteVisit(p: Profile, npc: string, now = Date.now()) {
  const m = memoryOf(p, npc);
  m.met++;
  m.lastAt = now;
  m.lastLevel = pubLevel(p);
  m.seenLogAt = p.log[0]?.at ?? now;
}

export function noteTalk(p: Profile, npc: string, said: string) {
  const m = memoryOf(p, npc);
  m.talk.push(said.slice(0, 200));
  if (m.talk.length > 6) m.talk.shift();
  bump(p, 'talks');
}

/** Ergebnis eines Kneipenspiels gegen die Figur (won = Spielerin hat gewonnen) */
export function noteGame(p: Profile, npc: string, game: string, won: boolean, now = Date.now()) {
  const m = memoryOf(p, npc);
  if (won) m.games.won++;
  else m.games.lost++;
  m.games.last = { game, won, at: now };
  bump(p, 'games');
  if (won) bump(p, 'games_won');
}

/** Stichpunkte für das Sprachmodell: was die Figur über Mara weiß */
export function memorySummary(p: Profile, npc: string): string[] {
  const m = p.memory?.[npc];
  const out: string[] = [];
  if (!m || !m.met) out.push('Ihr trefft euch zum ersten Mal im Anker.');
  else out.push(`Ihr habt euch schon ${m.met}-mal im Anker getroffen.`);
  out.push(`Der Anker hat Ausbaustufe ${pubLevel(p)} von 5.`);
  const fav = FAV_CATEGORY[npc];
  const lastFav = p.log.find((e) => e.category === fav);
  if (lastFav) out.push(`Mara hat zuletzt in deiner Lieblingskategorie „${fav}“ die Frage „${lastFav.prompt}“ ${lastFav.right ? 'richtig' : 'falsch'} beantwortet (Lösung: ${lastFav.answer}).`);
  if (m?.games.last) out.push(`Beim letzten ${m.games.last.game} hat ${m.games.last.won ? 'Mara gegen dich gewonnen' : 'Mara gegen dich verloren'} (insgesamt ${m.games.won}:${m.games.lost} aus Maras Sicht).`);
  if (m?.talk.length) out.push(`Mara hat dir zuletzt erzählt: „${m.talk.slice(-2).join('“ / „')}“.`);
  return out;
}
